import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  RefObject,
} from "react";
import { styled, useTheme } from "@mui/material";
import { CitationMap } from "fe-web-components/references/types";
import {
  FeedbackInput,
  FeedbackStates,
} from "fe-web-components/feedback/types";
import ContainedButton from "fe-web-components/layout/buttons/ContainedButton";
import {
  LineActions,
  LineActionsButton,
} from "fe-web-components/layout/buttons/LineActions";
import { FeedbackForm } from "../feedback/FeedbackForm";
import { ReactComponent as ChevronDown } from "fe-web-components/assets/chevronDown.svg";
import { ReactComponent as ChevronUp } from "fe-web-components/assets/chevronUp.svg";
import { ReactComponent as CopyIcon } from "fe-web-components/assets/copyIcon2.svg";
import { ReactComponent as ThumbsUp } from "fe-web-components/assets/thumbsUpIcon.svg";
import { ReactComponent as ThumbsDown } from "fe-web-components/assets/thumbsDownIcon.svg";
import { ReactComponent as CheckIcon } from "fe-web-components/assets/checkIcon.svg";
import { LLMDisclaimer } from "fe-web-components/layout/LLMDisclaimer";
import { useTranslateByGender } from "fe-common/locale/useTranslateByGender";
import highlightElement from "fe-common/utils/highlightElement";
import { Reference } from "./Reference";
import { MemoizedCustomFeedback } from "../feedback/CustomFeedback";
import useSessionStore from "rag-chat/src/context/useSessionStore";
//region [[ Styles ]]

const AnswerFooterView = styled("div")(({ theme }) => ({
  flexDirection: "column",
  fontSize: 12,
  paddingTop: 30,
  paddingBottom: 10,
  width: "100%",
  "& .feedback_cta": { marginRight: 5 },
  "& .feedback_cta:dir(rtl)": {
    marginLeft: 5,
    marginRight: 0,
  },
  [theme.breakpoints.down("sm")]: {
    paddingTop: 20,
    paddingBottom: 0,
    "& .references-button": {
      marginLeft: -44,
    },
    "& .references": {
      marginLeft: -44,
    },
    "&:dir(rtl) .references-button": {
      marginLeft: 0,
      marginRight: -44,
    },
    "&:dir(rtl) .references": {
      marginLeft: 0,
      marginRight: -44,
    },
  },
}));

const FooterWrapper = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 20,
  marginBottom: 20,
});

const References = styled("div")(({ theme }) => ({
  background: theme.palette.primary.light,
  border: `1px solid ${theme.palette.divider}`,
  fontSize: theme.typography.fontSize,
  direction: "ltr",
  position: "relative",
  borderRadius: 8,
  overflow: "hidden",
  "& ol, ul": {
    margin: 0,
    padding: "0 0 0 30px",
  },
  " & li": {
    padding: "0",
  },
  "&.open": {
    transition: "opacity 0.3s ease-in-out",
    height: "auto",
    opacity: 1,
    padding: 24,
    marginBottom: 20,
    [theme.breakpoints.down("sm")]: {
      padding: 14,
      fontSize: 13,
    },
  },
  "&.closed": {
    opacity: 0,
    height: 0,
  },
}));

const ReferenceItem = styled("div")({
  marginLeft: -30,
  padding: "12px 12px 12px 30px",
  "&.highlight": {
    animation: "flashAnimation 3s ease-in-out",
  },
  "@keyframes flashAnimation": {
    "0%": {
      background: "inherit",
    },
    "10%": {
      borderColor: "#E0E0E0",
      background: "#FFFFFF",
    },
    "90%": {
      borderColor: "#E0E0E0",
      background: "#FFFFFF",
    },
    "100%": {
      background: "inherit",
    },
  },
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface AnswerFooterProps {
  citations?: CitationMap;
  debugMode?: boolean;
  onCopy?: () => Promise<void>;
  onToggleRefs?: (open: boolean) => void;
  feedback?: FeedbackInput;
  rtlDisplay?: boolean;
  onSendFeedback?: (
    feedbackInput: FeedbackInput,
    showThankyou: boolean,
  ) => Promise<boolean>;
  highlightedRefId?: number;
}

export type FooterHandle = {
  highlightRef: (refId: number) => void;
};
//endregion [[ Props ]]

//region [[ Functions ]]

//endregion [[ Functions ]]

export const AnswerFooter = forwardRef<FooterHandle, AnswerFooterProps>(
  (
    {
      citations,
      onCopy,
      onToggleRefs,
      feedback,
      debugMode,
      rtlDisplay,
      onSendFeedback,
    }: AnswerFooterProps,
    ref,
  ) => {
    const [copied, setCopied] = useState<boolean>(false);
    const [feedbackState, setFeedbackState] = useState<FeedbackStates>(
      feedback?.positive == null
        ? FeedbackStates.none
        : feedback?.positive
        ? FeedbackStates.positive
        : FeedbackStates.negative,
    );
    const [showFeedback, setShowFeedback] = useState<boolean>(false);
    const [feedbackLocked, setFeedbackLocked] = useState<boolean>(
      feedback?.positive != null,
    );
    const [showRefs, setShowRefs] = useState<boolean>(false);
    const feedbackFormRef = useRef(null);
    const { ragSettings } = useSessionStore();
    const { palette } = useTheme();
    const { translate } = useTranslateByGender();

    //create refs for each citation
    const citationsRefs: RefObject<any>[] = useMemo(() => {
      if (!citations) return [];
      return citations
        ? Array.from([...citations.keys()]).map(() => React.createRef())
        : [];
    }, [citations]);

    //pass the function to parent component
    useImperativeHandle(
      ref,
      () => ({
        highlightRef: (refId: number) => {
          const citationRef = citationsRefs[refId];
          if (onToggleRefs) onToggleRefs(showRefs);
          setShowRefs(true);
          if (citationRef && citationRef.current) {
            setTimeout(() => {
              highlightElement(citationRef.current, true);
            }, 100); //highlight after delay to allow css transition
          }
        },
      }),
      [citationsRefs],
    );

    useEffect(() => {
      if (showFeedback && feedbackFormRef.current) {
        feedbackFormRef.current.scrollIntoView({
          behavior: "auto",
          block: "nearest",
        });
      }
    }, [showFeedback]);

    function showCopyNotification() {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }

    const handleSetFeedback = (state: FeedbackStates) => {
      if (state === feedbackState) {
        setShowFeedback(false);
        setFeedbackState(FeedbackStates.none);
      } else {
        setShowFeedback(true);
        setFeedbackState(state);
      }
    };
    return (
      <AnswerFooterView>
        <FooterWrapper dir={rtlDisplay ? "rtl" : ""}>
          {citations && citations.size > 0 && (
            <ContainedButton
              onClick={() => {
                if (onToggleRefs) onToggleRefs(showRefs);
                setShowRefs(!showRefs);
              }}
              endIcon={showRefs ? <ChevronUp /> : <ChevronDown />}
              className={`references-button ${showRefs ? "active" : ""}`}
            >
              {translate("REFERENCES")}
            </ContainedButton>
          )}
          {
            <LineActions>
              {ragSettings?.showFeedbackButtons && (
                <>
                  <span className="feedback_cta">
                    {translate("DIALOGUE_FEEDBACK_CTA")}
                  </span>
                  <LineActionsButton
                    onClick={() => handleSetFeedback(FeedbackStates.positive)}
                    title={
                      feedbackLocked ? translate("FEEDBACK_SENT") : "Helpful"
                    }
                    className={
                      feedbackState === FeedbackStates.positive ? "active" : ""
                    }
                    disabled={feedbackLocked}
                  >
                    <ThumbsUp />
                  </LineActionsButton>
                  <LineActionsButton
                    title={
                      feedbackLocked
                        ? translate("FEEDBACK_SENT")
                        : "Report an issue"
                    }
                    onClick={() => handleSetFeedback(FeedbackStates.negative)}
                    className={
                      feedbackState === FeedbackStates.negative ? "active" : ""
                    }
                    disabled={feedbackLocked}
                  >
                    <ThumbsDown />
                  </LineActionsButton>
                </>
              )}
              <LineActionsButton
                onClick={() => {
                  if (onCopy) onCopy().then(showCopyNotification);
                }}
                title={copied ? translate("COPIED") : translate("COPY")}
              >
                {copied ? (
                  <CheckIcon style={{ color: palette.primary.main }} />
                ) : (
                  <CopyIcon />
                )}
              </LineActionsButton>
            </LineActions>
          }
        </FooterWrapper>

        {citations && citations.size > 0 && (
          <References className={`references ${showRefs ? "open" : "closed"}`}>
            <LLMDisclaimer />
            <ol>
              {Array.from(citations.keys()).map((c, idx) => {
                const citation = citations.get(c);
                return (
                  <li className="citation" key={"citation" + idx}>
                    <ReferenceItem ref={citationsRefs[idx]}>
                      <Reference citation={citation} />
                    </ReferenceItem>
                  </li>
                );
              })}
            </ol>
          </References>
        )}
        {debugMode && (feedback?.predefinedText || feedback?.freeText) && (
          <LineActions
            style={{
              whiteSpace: "break-spaces",
              fontSize: "13px",
              height: "auto",
              marginTop: -8,
            }}
          >
            <div>
              <strong>{translate("FEEDBACK")}</strong>
              <br />
              {[feedback?.predefinedText, feedback?.freeText].join("\n")}
            </div>
          </LineActions>
        )}
        {showFeedback && feedbackState !== FeedbackStates.none && (
          <FeedbackForm
            ref={feedbackFormRef}
            feedbackState={feedbackState}
            rtlDisplay={rtlDisplay}
            onSend={async (updatedFeedback, showThankyou) => {
              const success = await onSendFeedback(
                {
                  ...feedback,
                  ...updatedFeedback,
                },
                showThankyou,
              );
              setFeedbackLocked(true);
              setShowFeedback(false);
              return success;
            }}
            onClose={() => {
              setFeedbackState(FeedbackStates.none);
              setShowFeedback(false);
            }}
          />
        )}
        {ragSettings?.customFeedback?.length > 0 && (
          <MemoizedCustomFeedback
            customFeedbackOptions={ragSettings.customFeedback}
            value={feedback?.customFeedback}
            rtlDisplay={rtlDisplay}
            translate={translate}
            onChange={(customFeedback) => {
              onSendFeedback({ ...feedback, customFeedback }, false);
            }}
          />
        )}
      </AnswerFooterView>
    );
  },
);
